import { FC, useState } from 'react';
import { EditNote } from '@eqi-investimentos/icons/web';
import { Box, Flex, Text } from '@eqi-investimentos/react';
import {
  Slider as ChakraSlider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  type SliderMarkProps as ChakraSliderMarkProps,
} from '@chakra-ui/react';
import { ModalEditSliderValue } from '@/modules/FinancialPlanning/ui/Retirement/ModalEditSliderValue';
import { IconButton } from '@/components/elements/IconButton';
import { formatToMonetary } from '@/utils/format';

export const SliderInput: FC<SliderInputProps> = ({
  label,
  min,
  max,
  isMonetary = false,
  step = 1,
  marks,
  onChange,
}) => {
  const [value, setValue] = useState(min);
  const [editSliderValue, setEditSliderValue] = useState<string>('');

  const handleValueChange = (v: number) => {
    setValue(v);
    onChange(v);
  };

  return (
    <>
      <ModalEditSliderValue
        isOpen={!!editSliderValue}
        value={editSliderValue}
        onClose={() => setEditSliderValue('')}
        onChangeSlideValue={(number) => handleValueChange(number)}
        options={{
          input: {
            min: min,
            max: max,
            placeholder: 'label',
            isCurrency: isMonetary,
          },
        }}
      />
      <Box h="fit-content" pb="1.5rem">
        <Flex justifyContent="space-between" alignItems="center">
          <Text size="l">{label}</Text>
          <Flex alignItems="center" gap="2xs">
            <Text fontSize="1.625rem" color="content.brand" fontWeight="medium">
              {isMonetary
                ? formatToMonetary({ value, maximumFractionDigits: 0 })
                : value}
            </Text>
            <IconButton
              ariaLabel="Editar valor slider"
              onButtonClick={() => setEditSliderValue(min.toString())}
              icon={EditNote}
              buttonProps={{
                w: 16,
                h: 16,
              }}
              iconProps={{
                color: 'content.brand',
                boxSize: 13,
              }}
            />
          </Flex>
        </Flex>
        <ChakraSlider
          data-testid="slider-input"
          min={min}
          max={max}
          onChange={handleValueChange}
          step={step}
          defaultValue={min}
        >
          {marks?.map((mark, i) => (
            <SliderMark
              key={i}
              {...{
                mt: '1rem',
                fontSize: 'xs',
                color: 'content.secondary',
                ...mark,
              }}
            >
              {isMonetary
                ? formatToMonetary({
                    value: mark.value,
                    maximumFractionDigits: 0,
                  })
                : mark.value}
            </SliderMark>
          ))}
          <SliderTrack h={8} rounded="full" bg="border.secondary">
            <SliderFilledTrack bg="content.brand" />
          </SliderTrack>
          <SliderThumb boxSize={20} bg="background.secondary" />
        </ChakraSlider>
      </Box>
    </>
  );
};

export type SliderInputProps = {
  label: string;
  min: number;
  max: number;
  step?: number;
  isMonetary?: boolean;
  onChange: (value: number) => void;
  marks?: Array<ChakraSliderMarkProps>;
};
